import apiClient from './apiClient';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

export interface BlockedKeyword {
  _id: string;
  keyword: string;
  category?: {
    _id: string;
    name: string;
  } | null;
  errorMessage?: string;
  isActive: boolean;
  caseSensitive: boolean;
  matchType: 'exact' | 'contains' | 'word';
  createdBy?: {
    _id: string;
    name: string;
    username: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface KeywordFilters {
  category?: string | null;
  isActive?: boolean;
  search?: string;
}

class KeywordService {
  async getKeywords(filters: KeywordFilters = {}) {
    const params = new URLSearchParams();
    if (filters.category !== undefined) {
      params.append('category', filters.category === null ? 'null' : filters.category);
    }
    if (filters.isActive !== undefined) {
      params.append('isActive', filters.isActive.toString());
    }
    if (filters.search) {
      params.append('search', filters.search);
    }

    const response = await apiClient.get<ApiResponse<BlockedKeyword[]>>(
      `/keywords${params.toString() ? '?' + params.toString() : ''}`
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch keywords');
    }
    return response.data.data;
  }

  async getKeywordById(id: string) {
    const response = await apiClient.get<ApiResponse<BlockedKeyword>>(`/keywords/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch keyword');
    }
    return response.data.data;
  }

  async createKeyword(data: Partial<BlockedKeyword>) {
    const response = await apiClient.post<ApiResponse<BlockedKeyword>>('/keywords', data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create keyword');
    }
    return response.data.data;
  }

  async updateKeyword(id: string, data: Partial<BlockedKeyword>) {
    const response = await apiClient.put<ApiResponse<BlockedKeyword>>(`/keywords/${id}`, data);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update keyword');
    }
    return response.data.data;
  }

  async deleteKeyword(id: string) {
    const response = await apiClient.delete<ApiResponse<void>>(`/keywords/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete keyword');
    }
  }

  async toggleKeywordStatus(id: string) {
    const response = await apiClient.patch<ApiResponse<BlockedKeyword>>(`/keywords/${id}/toggle-status`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to toggle keyword status');
    }
    return response.data.data;
  }

  async testKeywordCheck(text: string, categoryId?: string | null) {
    const response = await apiClient.post<ApiResponse<{ blocked: boolean; keyword?: string; errorMessage?: string }>>(
      '/keywords/test',
      { text, categoryId }
    );
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to test keyword');
    }
    return response.data.data;
  }
}

export default new KeywordService();

